(function () {
  "use strict";

  const FOCUSABLE_SELECTORS = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ];

  const focusableQuery = FOCUSABLE_SELECTORS.join(",");

  if (!customElements.get("component-popup")) {
    class ComponentPopup extends HTMLElement {
      static activePopup = null;

      constructor() {
        super();
        this.dialog = this.querySelector("dialog");
        this.openButtons = [];
        this.closeButtons = [];
        this.activeTrigger = null;

        this.handleOpen = this.handleOpen.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
      }

      connectedCallback() {
        if (!this.dialog) return;

        if (!this.dialog.hasAttribute("role")) {
          this.dialog.setAttribute("role", "dialog");
        }
        this.dialog.setAttribute("aria-modal", "true");
        this.dialog.setAttribute("tabindex", "-1");

        this.openButtons = Array.from(this.querySelectorAll("[data-open-popup]"));
        this.closeButtons = Array.from(this.dialog.querySelectorAll("[data-close-popup]"));

        this.openButtons.forEach((button) => button.addEventListener("click", this.handleOpen));
        this.closeButtons.forEach((button) => button.addEventListener("click", this.handleClose));

        this.dialog.addEventListener("cancel", this.handleCancel);
        this.dialog.addEventListener("keydown", this.handleKeydown);
        this.dialog.addEventListener("click", this.handleOutsideClick);
      }

      disconnectedCallback() {
        this.openButtons.forEach((button) => button.removeEventListener("click", this.handleOpen));
        this.closeButtons.forEach((button) => button.removeEventListener("click", this.handleClose));

        if (!this.dialog) return;
        this.dialog.removeEventListener("cancel", this.handleCancel);
        this.dialog.removeEventListener("keydown", this.handleKeydown);
        this.dialog.removeEventListener("click", this.handleOutsideClick);
      }

      open(trigger) {
        if (!this.dialog) return;

        if (ComponentPopup.activePopup && ComponentPopup.activePopup !== this) {
          ComponentPopup.activePopup.close();
        }

        this.activeTrigger = trigger || this.activeTrigger || this.openButtons[0] || document.activeElement;

        if (this.dialog.hasAttribute("open")) {
          this.focusInitialElement();
          ComponentPopup.activePopup = this;
          return;
        }

        if (typeof this.dialog.showModal === "function") {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute("open", "");
        }

        ComponentPopup.activePopup = this;
        this.dialog.classList.add("is-open");
        this.focusInitialElement();
      }

      close() {
        if (!this.dialog || !this.dialog.hasAttribute("open")) return;

        if (typeof this.dialog.close === "function") {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute("open");
        }

        this.dialog.classList.remove("is-open");
        ComponentPopup.activePopup = ComponentPopup.activePopup === this ? null : ComponentPopup.activePopup;
        this.restoreFocus();
      }

      handleOpen(event) {
        if (event) event.preventDefault();
        this.open(event?.currentTarget);
      }

      handleClose(event) {
        if (event) event.preventDefault();
        this.close();
      }

      handleKeydown(event) {
        if (event.key === "Escape") {
          event.preventDefault();
          this.close();
          return;
        }

        if (event.key === "Tab") {
          this.trapFocus(event);
        }
      }

      handleOutsideClick(event) {
        if (event.target === this.dialog) {
          this.close();
        }
      }

      handleCancel(event) {
        event.preventDefault();
        this.close();
      }

      getFocusableElements() {
        return Array.from(this.dialog.querySelectorAll(focusableQuery)).filter(
          (element) =>
            !element.hasAttribute("disabled") &&
            element.getAttribute("tabindex") !== "-1" &&
            element.offsetParent !== null
        );
      }

      focusInitialElement() {
        const focusable = this.getFocusableElements();
        const target = focusable[0] || this.dialog.querySelector(".dialog-container") || this.dialog;
        target.focus({ preventScroll: true });
      }

      restoreFocus() {
        if (this.activeTrigger && typeof this.activeTrigger.focus === "function") {
          this.activeTrigger.focus({ preventScroll: true });
        }
      }

      trapFocus(event) {
        const focusable = this.getFocusableElements();

        if (!focusable.length) {
          event.preventDefault();
          this.dialog.focus({ preventScroll: true });
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    customElements.define("component-popup", ComponentPopup);
  }
})();
